/**
 * Centralized applicability/disabled-state rules for the Properties Panel's five sections.
 *
 * A section header is always clickable — this module only decides whether the *controls inside*
 * a section apply to the current selection, and which reason tag (if any) explains why they
 * don't. Every panel's top-level `disabled` prop should trace back to this function instead of
 * re-deriving selection composition (text presence, photo count, group-ness, lock state) inline.
 */
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { isGroupShape } from '@/lib/groups/group-kind'
import { isShapeTextFrame } from '@/lib/shapes/shape-text-frame'
import { resolvePlacedPhotoIdentity } from '@/lib/photos/fill-shape-with-photo'
import { resolvePlacedClikArtId } from '@/lib/clikart/sync-clikart-placement-from-shapes'
import type { PropertiesPanelReasonTag } from './types/properties-panel-reason-tag'
import type { PropertiesPanelSectionApplicability } from './types/properties-panel-section-applicability'
import type { PropertiesPanelPhotoApplicability } from './types/properties-panel-photo-applicability'
import type { PropertiesPanelApplicability } from './types/properties-panel-applicability'
import type { ComputePropertiesPanelApplicabilityInput } from './types/compute-properties-panel-applicability-input'

export const PROPERTIES_PANEL_REASON_TAGS = [
  'no selection',
  'locked',
  'grouped',
  'no text',
  'no photo',
  'multiple photos',
  'mixed selection',
] as const

/** Title Case display label for each reason tag — the tag itself stays lowercase since it's used
 *  as an identifier (applicability results, tests); this is only for the on-screen badge text. */
export const PROPERTIES_PANEL_REASON_TAG_LABELS: Record<PropertiesPanelReasonTag, string> = {
  'no selection': 'No Selection',
  locked: 'Locked',
  grouped: 'Grouped',
  'no text': 'No Text',
  'no photo': 'No Photo',
  'multiple photos': 'Multiple Photos',
  'mixed selection': 'Mixed Selection',
}

const APPLICABLE: PropertiesPanelSectionApplicability = { applicable: true, reason: null }
const PHOTO_APPLICABLE: PropertiesPanelPhotoApplicability = {
  applicable: true,
  reason: null,
  readOnlyPhoto: null,
}

function disabledBy(reason: PropertiesPanelReasonTag): PropertiesPanelSectionApplicability {
  return { applicable: false, reason }
}

function photoDisabledBy(
  reason: PropertiesPanelReasonTag,
  readOnlyPhoto: ShapeData | null = null
): PropertiesPanelPhotoApplicability {
  return { applicable: false, reason, readOnlyPhoto }
}

const ALL_DISABLED_BY_NO_SELECTION: PropertiesPanelApplicability = (() => {
  const noSelection = disabledBy('no selection')

  return {
    object: noSelection,
    type: noSelection,
    photo: photoDisabledBy('no selection'),
    layout: noSelection,
    effects: noSelection,
  }
})()

/** A group's own `paragraphs`/`photoFill` never count — "a group counts as one object," never as
 *  a stand-in for the text/photo it may contain. Exported for reuse by
 *  `properties-panel-default-section.ts`, which classifies a selection the same way to decide
 *  which section should open automatically. */
export function isTextCandidate(shape: ShapeData): boolean {
  return !isGroupShape(shape) && isShapeTextFrame(shape)
}

export function isPhotoCandidate(shape: ShapeData): boolean {
  return !isGroupShape(shape) && resolvePlacedPhotoIdentity(shape) != null
}

/** A placed ClikArt: a shape whose `photoFill` carries a `pezId` and no `imageId`. */
export function isClikArtCandidate(shape: ShapeData): boolean {
  return !isGroupShape(shape) && resolvePlacedClikArtId(shape) != null
}

/** The Photo section's slot is shared: it shows a placed photo or a placed ClikArt (relabelled,
 *  with its own restricted actions — see `SelectTool`). Both count toward "exactly one" and
 *  "multiple". */
export function isPhotoSlotCandidate(shape: ShapeData): boolean {
  return isPhotoCandidate(shape) || isClikArtCandidate(shape)
}

/** Every descendant (direct or nested, via further sub-groups) of the group `groupId`, found by
 *  walking `parentGroupId` links in `allShapes`. Used only to answer "does this group contain
 *  text/a photo" for the reason tag / read-only display — never to expose these shapes as edit
 *  targets. */
function collectGroupDescendants(groupId: string, allShapes: ShapeData[]): ShapeData[] {
  const childrenByParentId = new Map<string, ShapeData[]>()

  for (const shape of allShapes) {
    if (!shape.parentGroupId) continue

    const siblings = childrenByParentId.get(shape.parentGroupId) ?? []

    siblings.push(shape)
    childrenByParentId.set(shape.parentGroupId, siblings)
  }

  const descendants: ShapeData[] = []
  const queue = [...(childrenByParentId.get(groupId) ?? [])]

  while (queue.length > 0) {
    const shape = queue.shift()

    if (!shape) continue

    descendants.push(shape)

    if (isGroupShape(shape)) {
      queue.push(...(childrenByParentId.get(shape.id) ?? []))
    }
  }

  return descendants
}

export function computePropertiesPanelApplicability({
  shapes,
  textBoxes,
  hasLockedSelection,
  allShapes = [],
}: ComputePropertiesPanelApplicabilityInput): PropertiesPanelApplicability {
  const selection = [...shapes, ...textBoxes]
  const totalCount = selection.length

  if (totalCount === 0) return ALL_DISABLED_BY_NO_SELECTION

  // Object always works for any non-empty selection, including a locked or grouped one — its own
  // per-field disabling (`panelGeometryDisabled`/`panelControlsDisabled` in
  // `object-panel/helpers/panel-disabled-state.ts`) keeps the Lock switch itself clickable while
  // locked, so Object is never blanket-disabled here.
  const object = APPLICABLE

  // Layout/Effects work for any selection, grouped or not — only a lock blocks them outright.
  const layoutEffects = hasLockedSelection ? disabledBy('locked') : APPLICABLE

  const isSingleGroup = totalCount === 1 && isGroupShape(selection[0])
  const groupDescendants = isSingleGroup ? collectGroupDescendants(selection[0].id, allShapes) : []

  const type = ((): PropertiesPanelSectionApplicability => {
    if (hasLockedSelection) return disabledBy('locked')
    if (selection.some(isTextCandidate)) return APPLICABLE

    if (isSingleGroup) {
      // Only "grouped" when the group actually has text somewhere inside it — a text-free group
      // (e.g. two plain shapes) reads as plain "no text", same as any other non-text selection.
      return groupDescendants.some(isTextCandidate) ? disabledBy('grouped') : disabledBy('no text')
    }

    return disabledBy('no text')
  })()

  const photo = ((): PropertiesPanelPhotoApplicability => {
    if (hasLockedSelection) return photoDisabledBy('locked')

    if (isSingleGroup) {
      const groupPhotos = groupDescendants.filter(isPhotoSlotCandidate)

      // Still disabled either way — a group's contents are never directly editable here — but
      // exactly one photo or ClikArt inside gets surfaced for read-only display (same "exactly
      // one, nothing ambiguous" rule as a direct selection, one level down into the group).
      return photoDisabledBy('grouped', groupPhotos.length === 1 ? groupPhotos[0] : null)
    }

    const photosInSelection = selection.filter(isPhotoSlotCandidate)

    if (photosInSelection.length === 0) return photoDisabledBy('no photo')
    if (photosInSelection.length > 1) return photoDisabledBy('multiple photos')

    // Exactly one photo: applicable only when it's the entire selection — otherwise it's a mixed
    // selection, but that one real photo is still unambiguous, so it's surfaced read-only.
    return totalCount === 1
      ? PHOTO_APPLICABLE
      : photoDisabledBy('mixed selection', photosInSelection[0])
  })()

  return { object, type, photo, layout: layoutEffects, effects: layoutEffects }
}
